import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HoverView } from 'component/basics';
import { UtilityText } from 'component/text';
import React from 'react';
import type { TLabel } from 'util/stdtypes';

export type PaginationButtonState = 'hover' | 'press';

export type PaginationButtonProps = {
    ariaLabel?: TLabel;
    icon?: React.ComponentType<{ size: number }>;
    page?: number;
    disabled?: boolean;
    selected?: boolean;
    /** Force a visual state for stories. Not for production use. */
    forceState?: PaginationButtonState;
    onPress?: () => void;
};

export function PaginationButton({
    ariaLabel,
    icon: Icon,
    page,
    disabled = false,
    selected = false,
    forceState,
    onPress,
}: PaginationButtonProps) {
    const { colors } = useTheme();
    const s = usePaginationButtonStyles();

    const content = Icon ? (
        <Icon size={16} />
    ) : (
        <UtilityText
            color={selected ? colors.colorTextPrimary : colors.colorTextSecondary}
            text={String(page)}
            type='small'
            weight='medium'
        />
    );

    const stateStyle = forceState === 'hover' ? s.hover : forceState === 'press' ? s.press : null;

    return (
        <HoverView
            ariaLabel={ariaLabel}
            disabled={disabled}
            hoverStyle={s.hover}
            onPress={onPress}
            pressedStyle={s.press}
            style={[s.button, selected && s.selected, disabled && s.disabled, stateStyle]}
        >
            {content}
        </HoverView>
    );
}

const usePaginationButtonStyles = createThemedStyles((tokens) => ({
    button: {
        height: 32,
        minWidth: 32,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 32,
        borderWidth: 1,
        borderColor: 'transparent',
        paddingHorizontal: 4,
    },
    hover: {
        backgroundColor: 'rgba(0,0,0,0.1)',
    },
    press: {
        backgroundColor: 'rgba(0,0,0,0.2)',
    },
    selected: {
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        borderColor: 'transparent',
    },
    disabled: {
        opacity: 0.4,
    },
}));
